import { getStoredUser, apiGet, apiPost, apiPut, apiDelete } from './api';

export interface NewsTopic {
  id: string;
  title: string;
  category: 'stocks' | 'ai' | 'hardware' | 'regional' | 'general';
  description: string;
  enabled: boolean;
}

export interface NewsState {
  topics: NewsTopic[];
  personalityTone: 'analytical' | 'fast_summary' | 'deep_technical' | 'news_standard';
  lastBriefingMarkdown: string;
  lastUpdatedTime?: string;
}

export const NEWS_STORAGE_KEY = 'app_news_radar_data';

export const DEFAULT_NEWS_STATE: NewsState = {
  topics: [],
  personalityTone: 'analytical',
  lastBriefingMarkdown: '',
  lastUpdatedTime: undefined
};

// Filter out legacy hardcoded default topic IDs from older versions
function cleanTopics(topics: any[]): NewsTopic[] {
  if (!Array.isArray(topics)) return [];
  const legacyDefaultIds = ['topic_1', 'topic_2', 'topic_3', 'topic_4'];
  return topics.filter((t: any) => {
    if (!t || !t.title || !t.id) return false;
    if (legacyDefaultIds.includes(String(t.id))) return false;
    return true;
  });
}

export function normalizeNewsState(raw: any): NewsState {
  if (!raw) return DEFAULT_NEWS_STATE;
  const topics = cleanTopics(raw.topics || []);
  const lastBriefingMarkdown = (topics.length === 0 && (!raw.topics || raw.topics.length === 0))
    ? ''
    : (raw.lastBriefingMarkdown || '');
    
  return {
    topics,
    personalityTone: raw.personalityTone || 'analytical',
    lastBriefingMarkdown,
    lastUpdatedTime: raw.lastUpdatedTime
  };
}

let inMemoryNewsState: NewsState = DEFAULT_NEWS_STATE;
const listeners = new Set<(state: NewsState) => void>();

function notifyListeners() {
  const current = { ...inMemoryNewsState };
  listeners.forEach(cb => {
    try {
      cb(current);
    } catch (err) {
      console.error('Error notifying news listener:', err);
    }
  });
}

// Read news state from in-memory cache
export function getNewsState(): NewsState {
  return inMemoryNewsState;
}

// Save news state incrementally to news_radars and news_topics
export async function saveNewsState(state: NewsState): Promise<void> {
  const normalized = normalizeNewsState(state);
  inMemoryNewsState = normalized;
  notifyListeners();

  const user = getStoredUser();
  if (!user) return;

  try {
    // 1. Ensure radar channel exists
    let radarId = '';
    const radarsRes = await apiGet<any[]>('/api/news/radars');
    
    // Package state metadata inside channel_name
    const channelNamePayload = JSON.stringify({
      name: 'رادار خبر من',
      personalityTone: normalized.personalityTone,
      lastBriefingMarkdown: normalized.lastBriefingMarkdown,
      lastUpdatedTime: normalized.lastUpdatedTime
    });

    if (radarsRes.success && Array.isArray(radarsRes.data) && radarsRes.data.length > 0) {
      radarId = radarsRes.data[0].id;
      // Update metadata on server
      await apiPut(`/api/news/radars/${radarId}`, {
        channelName: channelNamePayload
      });
    } else {
      const createRes = await apiPost<any>('/api/news/radars', {
        channelName: channelNamePayload
      });
      if (createRes.success && createRes.data) {
        radarId = createRes.data.id;
      }
    }

    if (!radarId) return;

    // 2. Fetch current topics on server for diffing
    const serverTopicsRes = await apiGet<any[]>(`/api/news/radars/${radarId}/topics`);
    const serverTopics = serverTopicsRes.success && Array.isArray(serverTopicsRes.data) ? serverTopicsRes.data : [];

    const serverTopicsMap = new Map<string, any>();
    serverTopics.forEach(t => {
      // Decode topic details
      try {
        const payload = JSON.parse(t.topic_name || t.topicName || '{}');
        serverTopicsMap.set(t.id, { rawId: t.id, ...payload });
      } catch {
        serverTopicsMap.set(t.id, { rawId: t.id, title: t.topic_name || t.topicName });
      }
    });

    // 3. Add or update topics
    for (const topic of normalized.topics) {
      // Find matches in server map by client id (stored in payload)
      let matchedServerId = '';
      for (const [sId, sData] of serverTopicsMap.entries()) {
        if (sData.id === topic.id || sId === topic.id) {
          matchedServerId = sId;
          break;
        }
      }

      const topicNamePayload = JSON.stringify({
        id: topic.id,
        title: topic.title,
        category: topic.category,
        description: topic.description,
        enabled: topic.enabled
      });

      if (!matchedServerId) {
        // Create topic
        await apiPost(`/api/news/radars/${radarId}/topics`, {
          topicName: topicNamePayload
        });
      } else {
        // Check if changed
        const sData = serverTopicsMap.get(matchedServerId);
        const changed = sData.title !== topic.title ||
                        sData.category !== topic.category ||
                        sData.description !== topic.description ||
                        Boolean(sData.enabled) !== topic.enabled;
        if (changed) {
          await apiPut(`/api/news/topics/${matchedServerId}`, {
            topicName: topicNamePayload
          });
        }
      }
    }

    // 4. Delete removed topics
    const clientIds = new Set(normalized.topics.map(t => t.id));
    for (const [sId, sData] of serverTopicsMap.entries()) {
      if (!clientIds.has(sData.id || sId)) {
        await apiDelete(`/api/news/topics/${sId}`);
      }
    }
  } catch (err) {
    console.warn('Failed auto-syncing news state to canonical API:', err);
  }
}

// Fetch from cloud once
export async function syncNewsDataFromCloud(): Promise<NewsState> {
  const user = getStoredUser();
  if (!user) {
    inMemoryNewsState = DEFAULT_NEWS_STATE;
    return DEFAULT_NEWS_STATE;
  }

  try {
    const radarsRes = await apiGet<any[]>('/api/news/radars');
    if (radarsRes.success && Array.isArray(radarsRes.data) && radarsRes.data.length > 0) {
      const radar = radarsRes.data[0];
      const channelName = radar.channel_name || radar.channelName || '';

      let personalityTone: NewsState['personalityTone'] = 'analytical';
      let lastBriefingMarkdown = '';
      let lastUpdatedTime: string | undefined;

      try {
        const metadata = JSON.parse(channelName);
        if (metadata && typeof metadata === 'object') {
          personalityTone = metadata.personalityTone || 'analytical';
          lastBriefingMarkdown = metadata.lastBriefingMarkdown || '';
          lastUpdatedTime = metadata.lastUpdatedTime;
        }
      } catch {
        // Not JSON, ignore
      }

      // Fetch topics
      const topicsRes = await apiGet<any[]>(`/api/news/radars/${radar.id}/topics`);
      const topics: NewsTopic[] = [];

      if (topicsRes.success && Array.isArray(topicsRes.data)) {
        topicsRes.data.forEach(t => {
          try {
            const payload = JSON.parse(t.topic_name || t.topicName || '{}');
            topics.push({
              id: payload.id || t.id,
              title: payload.title || t.topic_name || t.topicName || '',
              category: payload.category || 'general',
              description: payload.description || '',
              enabled: payload.enabled !== undefined ? payload.enabled : true
            });
          } catch {
            topics.push({
              id: t.id,
              title: t.topic_name || t.topicName || '',
              category: 'general',
              description: '',
              enabled: true
            });
          }
        });
      }

      inMemoryNewsState = {
        topics: cleanTopics(topics),
        personalityTone,
        lastBriefingMarkdown,
        lastUpdatedTime
      };
      notifyListeners();
    }
  } catch (e) {
    console.warn('Error fetching news data from D1 news API:', e);
  }
  return inMemoryNewsState;
}

// Real-time listener for news state
export function subscribeToNewsData(onUpdate: (state: NewsState) => void) {
  listeners.add(onUpdate);
  onUpdate({ ...inMemoryNewsState });

  syncNewsDataFromCloud().then((st) => onUpdate(st));

  return () => {
    listeners.delete(onUpdate);
  };
}
